import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  FormControlLabel,
} from '@mui/material';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import MovieIcon from '@mui/icons-material/Movie';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import { userFilesAPI } from '../api';
import { getApiBaseUrl } from '../utils/apiConfig';
import { uploadWithConcurrency } from '../utils/uploadWithConcurrency';
import {
  buildPublicFolderShareUrl,
  copyTextToClipboard,
  findPublicShareAncestor,
} from '../utils/publicShareUrl';
import {
  compareFiles,
  describeSkippedMoves,
  encodeFileDragPayload,
  FILE_DRAG_MIME,
  formatFileDate,
  groupFilesByFolder,
  isFileDragEvent,
  moveDraggedFilesToFolder,
  parseFileDragPayload,
  persistShowSubfolders,
  persistViewMode,
  readStoredShowSubfolders,
  readStoredViewMode,
} from '../utils/fileListView';

function formatFileSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function isImageName(name) {
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name || '');
}

function isVideoName(name) {
  return /\.(mp4|m4v|webm|ogv|ogg|mov)$/i.test(name || '');
}

function fileNameKey(name) {
  return String(name || '').trim().toLowerCase();
}

function moveFileBefore(files, fromStoredName, toStoredName) {
  const from = files.findIndex((file) => file.storedName === fromStoredName);
  const to = files.findIndex((file) => file.storedName === toStoredName);
  if (from < 0 || to < 0 || from === to) return files;
  const next = [...files];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next.map((file, i) => ({ ...file, sortIndex: i + 1 }));
}

function previewNeighborFiles(list, index) {
  if (!Array.isArray(list) || index < 0) return [];
  const out = [];
  if (index > 0) out.push(list[index - 1]);
  if (list[index]) out.push(list[index]);
  if (index + 1 < list.length) out.push(list[index + 1]);
  return out;
}

function FolderThumb({ apiBaseUrl, file }) {
  const thumbSrc = resolveUrl(apiBaseUrl, file.thumbnailUrl || file.fileUrl, { withAuth: true });
  const fullSrc = resolveUrl(apiBaseUrl, file.fileUrl, { withAuth: true });
  const [src, setSrc] = useState(thumbSrc);
  const [usedFallback, setUsedFallback] = useState(!file.thumbnailUrl);

  useEffect(() => {
    setSrc(thumbSrc);
    setUsedFallback(!file.thumbnailUrl);
  }, [thumbSrc, file.thumbnailUrl]);

  return (
    <img
      src={src}
      alt={file.fileName}
      loading="lazy"
      decoding="async"
      onError={() => {
        if (!usedFallback && fullSrc && fullSrc !== src) {
          setUsedFallback(true);
          setSrc(fullSrc);
        }
      }}
      draggable={false}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
  );
}

function resolveUrl(apiBaseUrl, path, { withAuth = false } = {}) {
  if (!path) return path;
  if (/^https?:\/\//i.test(path)) return path;
  let url = path;
  if (apiBaseUrl && path.startsWith('/api/')) {
    const origin = apiBaseUrl.replace(/\/api\/?$/, '');
    url = `${origin}${path}`;
  }
  if (withAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      const sep = url.includes('?') ? '&' : '?';
      url = `${url}${sep}token=${encodeURIComponent(token)}`;
    }
  }
  return url;
}

function downloadUrl(apiBaseUrl, folderId, storedName, { withAuth = true } = {}) {
  const base = apiBaseUrl ? apiBaseUrl.replace(/\/$/, '') : '';
  let url = `${base}/files/download/${folderId}/${encodeURIComponent(storedName)}`;
  if (withAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      url = `${url}?token=${encodeURIComponent(token)}`;
    }
  }
  return url;
}

function buildFolderPath(folders, folderId) {
  const byId = new Map(folders.map((f) => [f.id, f]));
  const chain = [];
  let cur = byId.get(folderId);
  while (cur) {
    chain.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) : null;
  }
  return chain;
}

function SendToChatDialog({ open, onClose, users, groups, currentUserId, onPick }) {
  const [query, setQuery] = useState('');
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);
  const q = query.trim().toLowerCase();
  const filteredUsers = users
    .filter((u) => u.id !== currentUserId)
    .filter((u) => !q || u.username.toLowerCase().includes(q));
  const filteredGroups = groups.filter((g) => !q || g.name.toLowerCase().includes(q));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Send to chat</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search chats..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </Box>
        <List dense sx={{ pt: 0 }}>
          {filteredUsers.length > 0 && (
            <ListItem sx={{ bgcolor: 'action.hover' }}>
              <Typography variant="overline" color="text.secondary">People</Typography>
            </ListItem>
          )}
          {filteredUsers.map((u) => (
            <ListItemButton key={`u-${u.id}`} onClick={() => onPick({ type: 'direct', user: u })}>
              <ListItemAvatar>
                <FolderIcon />
              </ListItemAvatar>
              <ListItemText primary={u.username} secondary={u.online ? 'Online' : 'Offline'} />
            </ListItemButton>
          ))}
          {filteredGroups.length > 0 && (
            <ListItem sx={{ bgcolor: 'action.hover' }}>
              <Typography variant="overline" color="text.secondary">Groups</Typography>
            </ListItem>
          )}
          {filteredGroups.map((g) => (
            <ListItemButton key={`g-${g.id}`} onClick={() => onPick({ type: 'group', group: g })}>
              <ListItemAvatar>
                <FolderIcon />
              </ListItemAvatar>
              <ListItemText primary={g.name} secondary={`${g.userIds.length} members`} />
            </ListItemButton>
          ))}
          {filteredUsers.length === 0 && filteredGroups.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No chats found</Typography>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}

function ShareSelectedLinkDialog({ open, url, onClose }) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(null);

  useEffect(() => {
    if (open) {
      setCopied(false);
      setCopyError(null);
    }
  }, [open]);

  const handleCopy = async () => {
    if (!url) return;
    const ok = await copyTextToClipboard(url);
    if (ok) {
      setCopied(true);
      setCopyError(null);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setCopyError('Could not copy automatically — select the link and copy it manually.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share selected files</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Anyone with this link can open the public folder and browse the selected files.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Share link"
          value={url || ''}
          InputProps={{ readOnly: true }}
          onFocus={(e) => e.target.select()}
        />
        {copyError && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {copyError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          disabled={!url}
        >
          {copied ? 'Copied' : 'Copy link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function FullscreenPreview({
  open,
  file,
  files = [],
  apiBaseUrl,
  onClose,
  onDownload,
  onPrev,
  onNext,
  showSubfolderName = false,
  rootFolderId = null,
}) {
  const touchStartX = useRef(null);
  const list = files.length ? files : file ? [file] : [];
  const currentIndex = file
    ? Math.max(
        0,
        list.findIndex(
          (f) => f.storedName === file.storedName && (!file.folderId || f.folderId === file.folderId)
        )
      )
    : 0;
  const current = list[currentIndex] || file;
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < list.length - 1;

  useEffect(() => {
    if (!open || !current) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') onPrev?.();
      if (e.key === 'ArrowRight') onNext?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, current, onPrev, onNext]);

  if (!current) return null;

  const getUrl = (item) => resolveUrl(apiBaseUrl, item.fileUrl, { withAuth: true });
  const isImage = isImageName(current.fileName);
  const isVideo = isVideoName(current.fileName);
  const neighborImages = previewNeighborFiles(list, currentIndex).filter((item) =>
    isImageName(item.fileName)
  );
  const subfolderLabel =
    showSubfolderName && current.folderName && current.folderId && current.folderId !== rootFolderId
      ? current.folderName
      : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.95)', display: 'flex', flexDirection: 'column', position: 'relative' } }}
    >
      {subfolderLabel ? (
        <Typography
          sx={{
            position: 'absolute',
            top: 12,
            left: 0,
            right: 0,
            zIndex: 3,
            textAlign: 'center',
            color: 'white',
            fontSize: '2.25rem',
            fontWeight: 700,
            lineHeight: 1.2,
            pointerEvents: 'none',
            px: 10,
            textShadow: '0 2px 8px rgba(0,0,0,0.85)',
          }}
        >
          {subfolderLabel}
        </Typography>
      ) : null}
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          color: 'white',
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
          <Typography variant="subtitle1" noWrap>
            {current.fileName}
          </Typography>
          {list.length > 1 && (
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {currentIndex + 1} / {list.length}
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton color="inherit" onClick={onDownload} title="Download">
            <DownloadIcon />
          </IconButton>
          <IconButton color="inherit" onClick={onClose} title="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Toolbar>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          p: 2,
          position: 'relative',
        }}
        onTouchStart={(e) => {
          touchStartX.current = e.changedTouches[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current == null) return;
          const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
          touchStartX.current = null;
          if (dx > 50) onPrev?.();
          if (dx < -50) onNext?.();
        }}
      >
        {list.length > 1 && (
          <IconButton
            color="inherit"
            onClick={onPrev}
            disabled={!canPrev}
            title="Previous"
            sx={{
              position: 'absolute',
              left: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.35)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
            }}
          >
            <ChevronLeftIcon fontSize="large" />
          </IconButton>
        )}
        {neighborImages.map((item) => {
          const isCurrent = item.storedName === current.storedName;
          return (
            <img
              key={item.storedName}
              src={getUrl(item)}
              alt={isCurrent ? item.fileName : ''}
              draggable={false}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: isCurrent && isImage ? 'block' : 'none',
              }}
            />
          );
        })}
        {isVideo ? (
          <video
            key={current.storedName || current.fileUrl}
            src={getUrl(current)}
            controls
            autoPlay
            playsInline
            preload="metadata"
            onTouchStart={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '100%' }}
          >
            Your browser cannot play this video. Download it instead.
          </video>
        ) : !isImage ? (
          <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 480 }}>
            <InsertDriveFileIcon sx={{ fontSize: 96, color: 'text.secondary' }} />
            <Typography variant="h6" sx={{ mt: 2, wordBreak: 'break-all' }}>
              {current.fileName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {formatFileSize(current.fileSize)}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              sx={{ mt: 3 }}
              onClick={onDownload}
            >
              Download
            </Button>
          </Paper>
        ) : null}
        {list.length > 1 && (
          <IconButton
            color="inherit"
            onClick={onNext}
            disabled={!canNext}
            title="Next"
            sx={{
              position: 'absolute',
              right: 8,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.35)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
              '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
            }}
          >
            <ChevronRightIcon fontSize="large" />
          </IconButton>
        )}
      </Box>
    </Dialog>
  );
}

export default function FilesPanel({
  currentUser,
  users,
  groups,
  onSendFileToChat,
  folders = [],
  selectedFolderId,
  onSelectFolder,
  onCreateFolder,
  folderViewSource = 'mine',
  filesEpoch = 0,
}) {
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadCompleted, setUploadCompleted] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadActive, setUploadActive] = useState(0);
  const [failedUploads, setFailedUploads] = useState([]);
  const [skippedUploads, setSkippedUploads] = useState([]);
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [apiBaseUrl, setApiBaseUrl] = useState('');
  const [sendPickerFiles, setSendPickerFiles] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [creating, setCreating] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [shareLinkUrl, setShareLinkUrl] = useState('');
  const [shareLinkBusy, setShareLinkBusy] = useState(false);
  const [viewMode, setViewMode] = useState(readStoredViewMode);
  const [showSubfolders, setShowSubfolders] = useState(readStoredShowSubfolders);
  const [sortBy, setSortBy] = useState('sortIndex');
  const [sortDir, setSortDir] = useState('asc');
  const [dragStoredName, setDragStoredName] = useState(null);
  const [dropTargetName, setDropTargetName] = useState(null);
  const fileInputRef = useRef(null);
  const dragMovedRef = useRef(false);
  const dragStoredNameRef = useRef(null);

  useEffect(() => {
    (async () => {
      setApiBaseUrl(await getApiBaseUrl());
    })();
  }, []);

  const loadFiles = useCallback(async (folderId) => {
    if (!folderId) {
      setFiles([]);
      return;
    }
    setLoadingFiles(true);
    try {
      const includeSubfolders = viewMode === 'thumbs' && showSubfolders;
      const response = await userFilesAPI.getFiles(folderId, { includeSubfolders });
      setFiles(response.data);
    } catch (err) {
      console.error('Failed to load files', err);
    } finally {
      setLoadingFiles(false);
    }
  }, [viewMode, showSubfolders]);

  useEffect(() => {
    loadFiles(selectedFolderId);
    setSelected(new Set());
    setSelectionMode(false);
    setIsDragOver(false);
    setSkippedUploads([]);
  }, [selectedFolderId, loadFiles, filesEpoch]);

  const path = useMemo(
    () => buildFolderPath(folders, selectedFolderId),
    [folders, selectedFolderId]
  );
  const selectedFolder = path.length > 0 ? path[path.length - 1] : null;
  const isReadOnly = !selectedFolder || selectedFolder.readOnly !== false;
  const publicShareFolder = useMemo(
    () => findPublicShareAncestor(folders, selectedFolderId),
    [folders, selectedFolderId]
  );
  const canShareSelected = folderViewSource === 'mine' && !!publicShareFolder;
  const canDragFiles = !isReadOnly;
  const includeSubfolders = viewMode === 'thumbs' && showSubfolders;
  const canReorder = viewMode === 'thumbs' && canDragFiles && !selectionMode && !includeSubfolders;

  useEffect(() => {
    persistShowSubfolders(showSubfolders);
  }, [showSubfolders]);

  useEffect(() => {
    persistViewMode(viewMode);
  }, [viewMode]);

  const displayedFiles = useMemo(() => {
    if (viewMode !== 'details') return files;
    const list = [...files];
    list.sort((a, b) => compareFiles(a, b, sortBy, sortDir));
    return list;
  }, [files, viewMode, sortBy, sortDir]);

  const thumbGroups = useMemo(
    () => (includeSubfolders
      ? groupFilesByFolder(files, selectedFolderId)
      : [{ folderId: selectedFolderId, folderName: '', isRoot: true, files }]),
    [files, includeSubfolders, selectedFolderId]
  );

  const openPreview = useCallback((file, list = displayedFiles) => {
    const idx = list.findIndex((f) => f.storedName === file.storedName);
    setPreviewIndex(idx >= 0 ? idx : 0);
    setPreview(file);
  }, [displayedFiles]);

  const closePreview = useCallback(() => {
    setPreview(null);
    setPreviewIndex(0);
  }, []);

  const goPreview = useCallback((delta) => {
    setPreviewIndex((current) => {
      const next = current + delta;
      if (next < 0 || next >= displayedFiles.length) return current;
      setPreview(displayedFiles[next]);
      return next;
    });
  }, [displayedFiles]);

  const uploadFiles = useCallback(async (fileList) => {
    if (!selectedFolderId || fileList.length === 0 || isReadOnly) return;
    const batch = Array.from(fileList);
    const occupied = new Set(files.map((file) => fileNameKey(file.fileName)).filter(Boolean));
    const skipped = [];
    const toUpload = [];
    for (const file of batch) {
      const key = fileNameKey(file.name);
      if (!key) continue;
      if (occupied.has(key)) {
        skipped.push(file);
        continue;
      }
      occupied.add(key);
      toUpload.push(file);
    }
    setSkippedUploads(skipped.map((file) => file.name));
    setFailedUploads([]);
    setRetryingFailed(false);
    if (toUpload.length === 0) return;

    setUploading(true);
    setUploadTotal(toUpload.length);
    setUploadCompleted(0);
    setUploadProgress(0);
    setUploadActive(0);

    const runBatch = async (filesToUpload, concurrency) => {
      setUploadTotal(filesToUpload.length);
      setUploadCompleted(0);
      setUploadProgress(0);
      setUploadActive(0);
      const { failed } = await uploadWithConcurrency({
        files: filesToUpload,
        concurrency,
        uploadOne: (file, onFileProgress) =>
          userFilesAPI.uploadFile(selectedFolderId, file, onFileProgress),
        onProgress: ({ percent, completed, total, active }) => {
          setUploadProgress(percent);
          setUploadCompleted(completed);
          setUploadTotal(total);
          setUploadActive(active);
        },
        onFileFailed: (file) => {
          setFailedUploads((prev) => (prev.includes(file) ? prev : [...prev, file]));
        },
        onFileSucceeded: (file) => {
          setFailedUploads((prev) => prev.filter((f) => f !== file));
        },
      });
      return failed.map((item) => item.file);
    };

    try {
      let remaining = await runBatch(toUpload, 4);
      await loadFiles(selectedFolderId);
      if (remaining.length > 0) {
        setFailedUploads(remaining);
        setRetryingFailed(true);
        remaining = await runBatch(remaining, 2);
        await loadFiles(selectedFolderId);
        setFailedUploads(remaining);
      }
    } catch (err) {
      console.error('Upload failed', err);
    } finally {
      setUploading(false);
      setRetryingFailed(false);
      setUploadProgress(0);
      setUploadCompleted(0);
      setUploadTotal(0);
      setUploadActive(0);
    }
  }, [selectedFolderId, loadFiles, isReadOnly, files]);

  const persistFileOrder = useCallback(async (ordered) => {
    if (!selectedFolderId || isReadOnly) return;
    try {
      const res = await userFilesAPI.reorderFiles(
        selectedFolderId,
        ordered.map((file) => file.storedName)
      );
      if (Array.isArray(res.data)) setFiles(res.data);
    } catch (err) {
      console.error('Failed to save file order', err);
      await loadFiles(selectedFolderId);
    }
  }, [selectedFolderId, isReadOnly, loadFiles]);

  const handleSortColumn = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    setSortDir(column === 'uploadedAt' || column === 'fileSize' ? 'desc' : 'asc');
  };

  const handleCardDragStart = (e, file) => {
    if (!canDragFiles) return;
    if (e.target.closest('button, a, input')) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    dragMovedRef.current = false;
    const sourceFolderId = file.folderId || selectedFolderId;
    const names = selected.has(file.storedName) && selected.size > 0
      ? files
          .filter((item) => selected.has(item.storedName) && (item.folderId || selectedFolderId) === sourceFolderId)
          .map((item) => item.storedName)
      : [file.storedName];
    dragStoredNameRef.current = file.storedName;
    setDragStoredName(file.storedName);
    e.dataTransfer.effectAllowed = 'move';
    try {
      e.dataTransfer.setData(FILE_DRAG_MIME, '1');
    } catch {
      /* some browsers only allow text/plain */
    }
    e.dataTransfer.setData(
      'text/plain',
      encodeFileDragPayload({ folderId: sourceFolderId, storedNames: names })
    );
  };

  const handleCardDragOver = (e, file) => {
    if (!canReorder) return;
    const from = dragStoredNameRef.current;
    if (!from) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    if (from !== file.storedName && dropTargetName !== file.storedName) {
      setDropTargetName(file.storedName);
    }
  };

  const handleCardDrop = async (e, file) => {
    if (!canReorder) return;
    e.preventDefault();
    e.stopPropagation();
    const payload = parseFileDragPayload(e.dataTransfer.getData('text/plain') || '');
    const fromName = payload?.storedNames?.length === 1
      ? payload.storedNames[0]
      : dragStoredNameRef.current;
    dragStoredNameRef.current = null;
    setDropTargetName(null);
    setDragStoredName(null);
    if (!fromName || fromName === file.storedName) return;
    if (payload?.storedNames?.length > 1) return;
    dragMovedRef.current = true;
    const next = moveFileBefore(files, fromName, file.storedName);
    if (next === files) return;
    setFiles(next);
    await persistFileOrder(next);
  };

  const handleDropOnFolder = async (e, folder) => {
    if (!folder || folder.readOnly !== false || folder.id === selectedFolderId) return;
    if (!isFileDragEvent(e) && !parseFileDragPayload(e.dataTransfer.getData('text/plain') || '')) return;
    try {
      const result = await moveDraggedFilesToFolder({
        event: e,
        targetFolderId: folder.id,
        moveFiles: userFilesAPI.moveFiles,
      });
      if (!result) return;
      dragMovedRef.current = true;
      const message = describeSkippedMoves(result.skipped);
      if (message) alert(message);
      if (result.moved?.length) {
        setSelected(new Set());
        setSelectionMode(false);
        await loadFiles(selectedFolderId);
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to move files.');
    }
  };

  const handleCardDragEnd = () => {
    dragStoredNameRef.current = null;
    setDragStoredName(null);
    setDropTargetName(null);
  };

  const handleFileUpload = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (fileInputRef.current) fileInputRef.current.value = '';
    await uploadFiles(fileList);
  };

  const handleDragOver = (e) => {
    if (!selectedFolderId || isReadOnly) return;
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    if (!selectedFolderId) return;
    const nextTarget = e.relatedTarget;
    if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e) => {
    if (!selectedFolderId || isReadOnly) return;
    const droppedFiles = Array.from(e.dataTransfer?.files || []);
    setIsDragOver(false);
    if (droppedFiles.length === 0) return;
    e.preventDefault();
    await uploadFiles(droppedFiles);
  };

  const handleDeleteFile = async (file) => {
    const folderId = file.folderId || selectedFolderId;
    if (!folderId) return;
    if (!window.confirm(`Delete "${file.fileName}"?`)) return;
    try {
      await userFilesAPI.deleteFile(folderId, file.storedName);
      await loadFiles(selectedFolderId);
    } catch (err) {
      alert('Failed to delete file.');
    }
  };

  const handleDownloadFile = (file) => {
    const url = downloadUrl(apiBaseUrl, file.folderId || selectedFolderId, file.storedName);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSelected = () => {
    const items = files.filter((f) => selected.has(f.storedName));
    items.forEach((f, idx) => {
      setTimeout(() => handleDownloadFile(f), idx * 250);
    });
  };

  const handleDeleteSelected = async () => {
    const items = files.filter((f) => selected.has(f.storedName));
    if (items.length === 0) return;
    if (!window.confirm(`Delete ${items.length} selected file${items.length === 1 ? '' : 's'}?`)) return;
    try {
      for (const f of items) {
        await userFilesAPI.deleteFile(f.folderId || selectedFolderId, f.storedName);
      }
      setSelected(new Set());
      setSelectionMode(false);
      await loadFiles(selectedFolderId);
    } catch (err) {
      alert('Failed to delete selected files.');
    }
  };

  const handleSendOne = (file) => setSendPickerFiles([file]);
  const handleSendSelected = () => {
    const items = files.filter((f) => selected.has(f.storedName));
    if (items.length === 0) return;
    setSendPickerFiles(items);
  };

  const handleShareSelectedLink = async () => {
    const items = files.filter((f) => selected.has(f.storedName));
    if (!publicShareFolder || items.length === 0) return;
    setShareLinkBusy(true);
    try {
      const res = await userFilesAPI.getFolderPublicShare(publicShareFolder.id);
      const token = res.data?.shareToken;
      if (!token) {
        alert('This folder is not publicly shared. Turn on a public link for the folder first.');
        return;
      }
      setShareLinkUrl(
        buildPublicFolderShareUrl(window.location.origin, token, {
          folderId: selectedFolderId,
          storedNames:
            items.length === files.length ? [] : items.map((f) => f.storedName),
          openGallery: true,
          includeSubfolders,
        })
      );
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to create share link.');
    } finally {
      setShareLinkBusy(false);
    }
  };

  const handlePickChat = (target) => {
    if (!sendPickerFiles) return;
    const attachments = sendPickerFiles.map((f) => ({
      fileId: `${f.folderId}:${f.storedName}`,
      fileName: f.fileName,
      fileSize: f.fileSize,
      fileUrl: f.fileUrl,
      folderId: f.folderId,
    }));
    onSendFileToChat?.(target, attachments);
    setSendPickerFiles(null);
    setSelected(new Set());
    setSelectionMode(false);
  };

  const toggleSelected = (storedName) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(storedName)) next.delete(storedName);
      else next.add(storedName);
      return next;
    });
  };

  const allSelected = files.length > 0 && selected.size === files.length;
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(files.map((f) => f.storedName)));
  };

  const submitCreate = async () => {
    const trimmed = createName.trim();
    if (!trimmed) return;
    setCreating(true);
    try {
      const created = await onCreateFolder?.(trimmed, null);
      if (created?.id) onSelectFolder?.(created.id);
      setCreateOpen(false);
      setCreateName('');
    } catch (e) {
      alert(e?.response?.data?.error || 'Failed to create folder');
    } finally {
      setCreating(false);
    }
  };

  if (!selectedFolder) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          gap: 2,
          p: 3,
        }}
      >
        <FolderIcon sx={{ fontSize: 96 }} />
        <Typography variant="h6">
          {folders.length === 0 ? 'You have no folders yet' : 'Select a folder'}
        </Typography>
        <Typography variant="body2" textAlign="center">
          {folders.length === 0
            ? 'Create your first folder in the sidebar to get started.'
            : 'Choose a folder in the sidebar, or create a new one.'}
        </Typography>
        <Button
          variant="contained"
          startIcon={<CreateNewFolderIcon />}
          onClick={() => setCreateOpen(true)}
          sx={{ display: folderViewSource === 'mine' ? undefined : 'none' }}
        >
          New folder
        </Button>
        <Dialog open={createOpen} onClose={creating ? undefined : () => setCreateOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>New folder</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              label="Folder name"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitCreate();
              }}
              sx={{ mt: 1 }}
              disabled={creating}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button variant="contained" onClick={submitCreate} disabled={creating || !createName.trim()}>
              {creating ? <CircularProgress size={18} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', position: 'relative' }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragOver && !isReadOnly && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 20,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(25, 118, 210, 0.08)',
            border: '2px dashed',
            borderColor: 'primary.main',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: 'background.paper',
              color: 'primary.main',
              fontWeight: 700,
            }}
          >
            Drop files to upload
          </Typography>
        </Box>
      )}
      <Toolbar
        variant="dense"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 1,
          minHeight: 56,
          flexWrap: 'wrap',
        }}
      >
        <Breadcrumbs
          separator="›"
          sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          aria-label="folder breadcrumb"
        >
          {path.map((folder, i) => {
            const isLast = i === path.length - 1;
            const crumbSx = {
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            };
            const dropHandlers = !isLast && folder.readOnly === false
              ? {
                  onDragOver: (e) => {
                    if (!isFileDragEvent(e)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                  },
                  onDrop: (e) => handleDropOnFolder(e, folder),
                }
              : {};
            if (isLast) {
              return (
                <Typography key={folder.id} color="text.primary" noWrap sx={{ fontWeight: 600 }}>
                  {folder.name}
                </Typography>
              );
            }
            return (
              <Link
                key={folder.id}
                component="button"
                variant="body2"
                underline="hover"
                color="inherit"
                onClick={() => onSelectFolder?.(folder.id)}
                sx={crumbSx}
                {...dropHandlers}
              >
                {folder.name}
              </Link>
            );
          })}
        </Breadcrumbs>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={viewMode}
          onChange={(_, next) => {
            if (next) setViewMode(next);
          }}
          aria-label="Folder view mode"
        >
          <ToggleButton value="thumbs" aria-label="Thumbnail view" title="Thumbnails">
            <ViewModuleIcon fontSize="small" />
          </ToggleButton>
          <ToggleButton value="details" aria-label="Details view" title="Details">
            <ViewListIcon fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
        {viewMode === 'thumbs' && (
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showSubfolders}
                onChange={(e) => setShowSubfolders(e.target.checked)}
              />
            }
            label="Show subfolders"
            sx={{
              ml: 0.5,
              mr: 0.5,
              whiteSpace: 'nowrap',
              '& .MuiFormControlLabel-label': { fontSize: '0.875rem' },
            }}
          />
        )}
        {selectionMode ? (
          <>
            <Tooltip title={allSelected ? 'Deselect all' : 'Select all'}>
              <IconButton size="small" onClick={toggleSelectAll}>
                {allSelected ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {selected.size} selected
            </Typography>
            <Tooltip title="Download selected">
              <span>
                <IconButton size="small" onClick={handleDownloadSelected} disabled={selected.size === 0}>
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Send selected to chat">
              <span>
                <IconButton size="small" onClick={handleSendSelected} disabled={selected.size === 0}>
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
            {canShareSelected && (
              <Tooltip title="Create share link for selected files">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleShareSelectedLink}
                    disabled={selected.size === 0 || shareLinkBusy}
                  >
                    {shareLinkBusy ? <CircularProgress size={18} /> : <LinkIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Delete selected">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDeleteSelected}
                  disabled={selected.size === 0}
                  sx={{ display: isReadOnly ? 'none' : undefined }}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              onClick={() => {
                setSelectionMode(false);
                setSelected(new Set());
              }}
            >
              Done
            </Button>
          </>
        ) : (
          <>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSelectionMode(true)}
              disabled={files.length === 0}
            >
              Select
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              sx={{ display: isReadOnly ? 'none' : undefined }}
            >
              Upload
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </>
        )}
      </Toolbar>

      {(uploading || failedUploads.length > 0 || skippedUploads.length > 0) && (
        <Box sx={{ px: 2, py: 1 }}>
          {skippedUploads.length > 0 && (
            <Alert severity="info" sx={{ mb: uploading || failedUploads.length > 0 ? 1 : 0 }}>
              {skippedUploads.length === 1
                ? `${skippedUploads[0]} is already in this folder — skipped.`
                : `${skippedUploads.length} files already in this folder — skipped.`}
            </Alert>
          )}
          {uploading && (
            <>
              <Typography variant="caption" color="text.secondary">
                {retryingFailed
                  ? `Retrying ${uploadTotal} failed file${uploadTotal === 1 ? '' : 's'} (${uploadCompleted} of ${uploadTotal}${uploadActive ? `, ${uploadActive} active` : ''}) — ${uploadProgress}%`
                  : uploadTotal > 1
                    ? `Uploading ${uploadCompleted} of ${uploadTotal} files (${uploadActive} active) — ${uploadProgress}%`
                    : `Uploading… ${uploadProgress}%`}
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </>
          )}
          {failedUploads.length > 0 && (
            <Alert
              severity={uploading ? (retryingFailed ? 'info' : 'warning') : 'error'}
              sx={{ mt: uploading ? 1 : 0 }}
              action={
                !uploading ? (
                  <Button color="inherit" size="small" onClick={() => uploadFiles(failedUploads)}>
                    Retry
                  </Button>
                ) : undefined
              }
            >
              {uploading && !retryingFailed && (
                <>
                  {failedUploads.length} file{failedUploads.length === 1 ? '' : 's'} failed and will be retried at the end:
                </>
              )}
              {uploading && retryingFailed && (
                <>Retrying {failedUploads.length} failed file{failedUploads.length === 1 ? '' : 's'}…</>
              )}
              {!uploading && (
                <>
                  {failedUploads.length} file{failedUploads.length === 1 ? '' : 's'} could not be uploaded.
                </>
              )}
              <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
                {failedUploads.slice(0, 10).map((f) => (
                  <li key={`${f.name}-${f.size}-${f.lastModified}`}>{f.name}</li>
                ))}
                {failedUploads.length > 10 && (
                  <li>and {failedUploads.length - 10} more</li>
                )}
              </Box>
            </Alert>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto', p: viewMode === 'details' && files.length > 0 ? 0 : 2 }}>
        {loadingFiles ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : files.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <InsertDriveFileIcon sx={{ fontSize: 72, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {isReadOnly ? 'This folder is empty.' : 'This folder is empty. Upload your first file.'}
            </Typography>
          </Box>
        ) : viewMode === 'details' ? (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {selectionMode && <TableCell padding="checkbox" />}
                  <TableCell sortDirection={sortBy === 'sortIndex' ? sortDir : false} sx={{ width: 88 }}>
                    <TableSortLabel
                      active={sortBy === 'sortIndex'}
                      direction={sortBy === 'sortIndex' ? sortDir : 'asc'}
                      onClick={() => handleSortColumn('sortIndex')}
                    >
                      Index
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'fileName' ? sortDir : false}>
                    <TableSortLabel
                      active={sortBy === 'fileName'}
                      direction={sortBy === 'fileName' ? sortDir : 'asc'}
                      onClick={() => handleSortColumn('fileName')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'fileSize' ? sortDir : false} sx={{ width: 120 }}>
                    <TableSortLabel
                      active={sortBy === 'fileSize'}
                      direction={sortBy === 'fileSize' ? sortDir : 'desc'}
                      onClick={() => handleSortColumn('fileSize')}
                    >
                      Size
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={sortBy === 'uploadedAt' ? sortDir : false} sx={{ width: 200 }}>
                    <TableSortLabel
                      active={sortBy === 'uploadedAt'}
                      direction={sortBy === 'uploadedAt' ? sortDir : 'desc'}
                      onClick={() => handleSortColumn('uploadedAt')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  {!selectionMode && <TableCell align="right" sx={{ width: 176 }}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedFiles.map((file) => {
                  const isSelected = selected.has(file.storedName);
                  const isDragging = dragStoredName && (
                    file.storedName === dragStoredName
                    || (selected.has(file.storedName) && selected.has(dragStoredName))
                  );
                  return (
                    <TableRow
                      key={file.storedName}
                      hover
                      selected={isSelected}
                      draggable={canDragFiles}
                      onDragStart={(e) => handleCardDragStart(e, file)}
                      onDragEnd={handleCardDragEnd}
                      onClick={() => {
                        if (dragMovedRef.current) {
                          dragMovedRef.current = false;
                          return;
                        }
                        if (selectionMode) toggleSelected(file.storedName);
                        else openPreview(file);
                      }}
                      sx={{
                        cursor: canDragFiles ? 'grab' : 'pointer',
                        opacity: isDragging ? 0.45 : 1,
                      }}
                    >
                      {selectionMode && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelected(file.storedName)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{file.sortIndex || ''}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                          {isImageName(file.fileName) ? (
                            <Box sx={{ width: 32, height: 32, borderRadius: 0.5, overflow: 'hidden', flexShrink: 0, bgcolor: 'grey.100' }}>
                              <FolderThumb apiBaseUrl={apiBaseUrl} file={file} />
                            </Box>
                          ) : isVideoName(file.fileName) ? (
                            <MovieIcon fontSize="small" color="action" />
                          ) : (
                            <InsertDriveFileIcon fontSize="small" color="action" />
                          )}
                          <Tooltip title={file.fileName}>
                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                              {file.fileName}
                            </Typography>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                      <TableCell>{formatFileSize(file.fileSize)}</TableCell>
                      <TableCell>{formatFileDate(file.uploadedAt)}</TableCell>
                      {!selectionMode && (
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Preview / Fullscreen">
                            <IconButton size="small" onClick={() => openPreview(file)}>
                              <FullscreenIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => handleDownloadFile(file)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send to chat">
                            <IconButton size="small" onClick={() => handleSendOne(file)}>
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteFile(file)}
                              sx={{ display: isReadOnly ? 'none' : undefined }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box>
            {thumbGroups.map((group) => (
              <Box key={group.folderId || 'root'} sx={{ mb: includeSubfolders && !group.isRoot ? 3 : 0 }}>
                    {!group.isRoot && group.folderName ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          width: 'auto',
                          mx: -2,
                          px: 2,
                          my: 1.5,
                        }}
                      >
                        <Box sx={{ flex: 1, height: 2, bgcolor: '#000' }} />
                        <Typography
                          sx={{
                            color: '#000',
                            fontWeight: 700,
                            fontSize: '1.05rem',
                            lineHeight: 1.2,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {group.folderName}
                        </Typography>
                        <Box sx={{ flex: 1, height: 2, bgcolor: '#000' }} />
                      </Box>
                    ) : null}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                    gap: 2,
                  }}
                >
            {group.files.map((file) => {
              const isImage = isImageName(file.fileName);
              const isVideo = isVideoName(file.fileName);
              const isSelected = selected.has(file.storedName);
              const isDragging = dragStoredName && (
                file.storedName === dragStoredName
                || (selected.has(file.storedName) && selected.has(dragStoredName))
              );
              const isDropTarget = dropTargetName === file.storedName;
              return (
                <Paper
                  key={`${file.folderId || selectedFolderId}:${file.storedName}`}
                  variant="outlined"
                  draggable={canDragFiles}
                  onDragStart={(e) => handleCardDragStart(e, file)}
                  onDragOver={(e) => handleCardDragOver(e, file)}
                  onDrop={(e) => handleCardDrop(e, file)}
                  onDragEnd={handleCardDragEnd}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderColor: isSelected || isDropTarget ? 'primary.main' : 'divider',
                    borderWidth: isSelected || isDropTarget ? 2 : 1,
                    opacity: isDragging ? 0.45 : 1,
                    cursor: selectionMode ? 'pointer' : canDragFiles ? 'grab' : 'default',
                    '&:active': canDragFiles ? { cursor: 'grabbing' } : undefined,
                  }}
                  onClick={() => {
                    if (dragMovedRef.current) {
                      dragMovedRef.current = false;
                      return;
                    }
                    if (selectionMode) toggleSelected(file.storedName);
                  }}
                >
                  {selectionMode && (
                    <Checkbox
                      checked={isSelected}
                      onChange={() => toggleSelected(file.storedName)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: 4,
                        zIndex: 2,
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        p: 0.25,
                      }}
                    />
                  )}
                  <Box
                    sx={{
                      height: 140,
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: canReorder ? 'grab' : canDragFiles ? 'grab' : 'pointer',
                      overflow: 'hidden',
                    }}
                    onClick={(e) => {
                      if (selectionMode) return;
                      if (dragMovedRef.current) {
                        dragMovedRef.current = false;
                        return;
                      }
                      e.stopPropagation();
                      openPreview(file);
                    }}
                  >
                    {isImage ? (
                      <FolderThumb apiBaseUrl={apiBaseUrl} file={file} />
                    ) : isVideo ? (
                      <MovieIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                    ) : (
                      <InsertDriveFileIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
                    )}
                  </Box>
                  <Box sx={{ p: 1 }}>
                    <Tooltip title={file.fileName}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {file.fileName}
                      </Typography>
                    </Tooltip>
                    <Typography variant="caption" color="text.secondary">
                      {file.sortIndex ? `#${file.sortIndex} · ` : ''}
                      {formatFileSize(file.fileSize)}
                    </Typography>
                  </Box>
                  {!selectionMode && (
                    <Stack
                      direction="row"
                      sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Tooltip title="Preview / Fullscreen">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPreview(file);
                          }}
                        >
                          <FullscreenIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(file);
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send to chat">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendOne(file);
                          }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile(file);
                          }}
                          sx={{ display: isReadOnly ? 'none' : undefined }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Paper>
              );
            })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <SendToChatDialog
        open={!!sendPickerFiles}
        onClose={() => setSendPickerFiles(null)}
        users={users}
        groups={groups}
        currentUserId={currentUser?.id}
        onPick={handlePickChat}
      />
      <FullscreenPreview
        open={!!preview}
        file={preview}
        files={displayedFiles}
        apiBaseUrl={apiBaseUrl}
        onClose={closePreview}
        onDownload={() => preview && handleDownloadFile(preview)}
        onPrev={() => goPreview(-1)}
        onNext={() => goPreview(1)}
        showSubfolderName={includeSubfolders}
        rootFolderId={selectedFolderId}
      />
      <ShareSelectedLinkDialog
        open={!!shareLinkUrl}
        url={shareLinkUrl}
        onClose={() => setShareLinkUrl('')}
      />
    </Box>
  );
}
